'use client';

/**
 * Expandable Breadcrumbs (Client Component)
 *
 * Used only for long breadcrumb trails (4+ items) that need
 * expand/collapse functionality.
 */

import { useAriaLabels } from '@/hooks/useAriaLabels';

import { Link } from '@/lib/i18n/navigation';
import { useState } from 'react';
import {
  buildBreadcrumbJsonLd,
  ChevronSeparator,
  formatLabel,
  type BreadcrumbItem,
} from './Breadcrumbs.shared';
import styles from '../Breadcrumbs.module.scss';

interface BreadcrumbsExpandableProps {
  items: BreadcrumbItem[];
  className?: string;
  testId?: string;
  includeJsonLd?: boolean;
}

const Expander = ({ onClick }: { onClick: () => void }) => {
  const aria = useAriaLabels();

  return (
    <button
      className={styles.expander}
      onClick={onClick}
      aria-label={aria.action('showAllBreadcrumbs')}
      type="button"
    >
      <span className={styles.dot} />
      <span className={styles.dot} />
      <span className={styles.dot} />
    </button>
  );
};

Expander.displayName = 'Expander';

export const BreadcrumbsExpandable = ({
  items,
  className,
  testId = 'Breadcrumbs',
  includeJsonLd = true,
}: BreadcrumbsExpandableProps) => {
  const aria = useAriaLabels();
  const [isExpanded, setIsExpanded] = useState(false);

  const jsonLd = buildBreadcrumbJsonLd(items);

  if (isExpanded) {
    return (
      <nav
        className={`${styles.wrapper} ${className || ''}`}
        aria-label={aria.action('breadcrumb')}
        data-testid={testId}
      >
        <ol className={styles.list}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <li
                key={`${item.label}-${index}`}
                className={styles.item}
                data-testid={`${testId}-item-${index}`}
              >
                {index > 0 && <ChevronSeparator />}
                {isLast || !item.href ? (
                  <span
                    className={styles.current}
                    aria-current="page"
                    data-testid={`${testId}-current`}
                  >
                    {formatLabel(item.label)}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className={styles.link}
                    data-testid={`${testId}-link-${index}`}
                  >
                    {formatLabel(item.label)}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
        {includeJsonLd ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        ) : null}
      </nav>
    );
  }

  const firstItem = items[0];
  const lastItem = items[items.length - 1];

  return (
    <nav
      className={`${styles.wrapper} ${className || ''}`}
      aria-label={aria.action('breadcrumb')}
      data-testid={testId}
    >
      <ol className={styles.list}>
        <li className={styles.item} data-testid={`${testId}-item-0`}>
          {firstItem.href ? (
            <Link href={firstItem.href} className={styles.link} data-testid={`${testId}-link-0`}>
              {formatLabel(firstItem.label)}
            </Link>
          ) : (
            <span className={styles.current}>{formatLabel(firstItem.label)}</span>
          )}
          <ChevronSeparator />
          <Expander onClick={() => setIsExpanded(true)} />
        </li>

        <li className={styles.item} data-testid={`${testId}-item-last`}>
          <span className={styles.current} aria-current="page" data-testid={`${testId}-current`}>
            {formatLabel(lastItem.label)}
          </span>
        </li>
      </ol>
      {includeJsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </nav>
  );
};

BreadcrumbsExpandable.displayName = 'BreadcrumbsExpandable';
